import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Avatar,
} from '@material-ui/core';
import {
  PlayArrow,
  Assignment,
} from '@material-ui/icons';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import { fetchApiData } from '../../../utils/service';
import basePCUrl from '../../../Globals';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import Cookies from 'js-cookie';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  filterBar: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #061739 0%, #0055A2 50%, #428BC9 100%)',
    color: 'white',
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(1px)',
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  trainingCard: {
    borderRadius: 16,
    height: '100%',
    transition: 'all 0.3s ease',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 12px 40px rgba(0, 85, 162, 0.2)',
    },
  },
  thumbnail: {
    height: 160,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '16px 16px 0 0',
    position: 'relative',
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    fontWeight: 600,
    fontSize: '0.7rem',
  },
  durationBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    background: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  progressContainer: {
    marginTop: 12,
    marginBottom: 16,
  },
  takeTrainingButton: {
    background: 'linear-gradient(45deg, #0055A2, #428BC9)',
    color: 'white',
    fontWeight: 600,
    padding: '10px 20px',
    borderRadius: 8,
    '&:hover': {
      background: 'linear-gradient(45deg, #061739, #0055A2)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(0, 85, 162, 0.3)',
    },
  },
  completedButton: {
    background: 'linear-gradient(45deg, #0055A2, #428BC9)',
    color: 'white',
    fontWeight: 600,
    padding: '10px 20px',
    borderRadius: 8,
    '&:hover': {
      background: 'linear-gradient(45deg, #061739, #0055A2)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(0, 85, 162, 0.3)',
    },
  },
  retakeQuizButton: {
    fontWeight: 600,
    padding: '10px 20px',
    borderRadius: 8,
  },
  filterChips: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  filterChip: {
    fontWeight: 600,
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: 20,
    transition: 'all 0.3s ease',
    '&.active': {
      background: 'linear-gradient(45deg, #0055A2, #428BC9)',
      color: 'white',
      transform: 'scale(1.05)',
    },
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  statsCard: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    padding: theme.spacing(2),
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const UserTrainingInterface = ({ 
  accessLevel, 
  selectedClient, 
  getTranslation = (text) => text, 
  preferredLanguage = 'english',
  trainingData 
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const pageTitleColor = theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e';
  const history = useHistory();
  const { getAccessTokenSilently } = usePortalAuth();
  const [filterType, setFilterType] = useState('All');

  // Fetch user training data
  const { isLoading, data, refetch } = useQuery('user-training', async () => {
    const token = await getAccessTokenSilently();
    return fetchApiData(basePCUrl, token, 'userTraining', accessLevel, selectedClient?.clientId);
  });

  // Process training data with real status checking
  const processedTraining = useMemo(() => {
    // Use the training data from props first, then API data
    const rawData = trainingData?.trainingItems || data?.trainingItems || [];
    
    if (!Array.isArray(rawData)) return [];
    
    return rawData.map(item => {
      // Check cookies for completion status
      const cookieStatus = Cookies.get(item.modId);
      const isCompleted = item.status === 'Completed' || cookieStatus === 'Completed' || item.progress === 100;
      const isAssigned = item.status === 'required' || item.status === 'Required' || item.status === 'Assigned';
      
      return {
        ...item,
        actualStatus: isCompleted ? 'Complete' : (isAssigned ? 'Assigned' : 'Available'),
        progress: item.progress || 0,
        isCompleted,
        isAssigned,
      };
    });
  }, [trainingData, data]);

  // Filter training based on selected filter
  const filteredTraining = useMemo(() => {
    if (filterType === 'All') return processedTraining;
    if (filterType === 'Complete') return processedTraining.filter(item => item.isCompleted);
    if (filterType === 'Assigned') return processedTraining.filter(item => item.isAssigned && !item.isCompleted);
    return processedTraining;
  }, [processedTraining, filterType]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = processedTraining.length;
    const completed = processedTraining.filter(item => item.isCompleted).length;
    const assigned = processedTraining.filter(item => item.isAssigned && !item.isCompleted).length;
    const available = processedTraining.filter(item => !item.isAssigned && !item.isCompleted).length;
    
    return {
      total,
      completed,
      assigned,
      available,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [processedTraining]);

  const handleTakeTraining = (training) => {
    history.push(`/Dashboard/Training?pad=&id=${training.modId}`);
  };

  const handleTakeQuiz = (training) => {
    history.push(`/Dashboard/TrainingQuiz?pad=&id=${training.modId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete': return '#4caf50';
      case 'Assigned': return '#f44336';
      case 'Available': return '#2196f3';
      default: return '#ff9800';
    }
  };

  const getTrainingAssignedDate = (training) => {
    const raw =
      training?.assignedDate ??
      training?.assigned_date ??
      training?.assignedTimestamp ??
      training?.assigned_timestamp ??
      training?.dateAssigned ??
      training?.assignmentDate ??
      null;

    if (raw == null) return null;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
        return null;
      }
      return trimmed;
    }
    return raw;
  };

  const formatTrainingAssignedDate = (value) => {
    if (value == null || value === '') return '';
    // Support unix seconds/ms as well as ISO/date strings.
    const asString = String(value).trim();
    if (!asString || asString.toLowerCase() === 'null' || asString.toLowerCase() === 'undefined') {
      return '';
    }
    const numeric = Number(value);
    const parsed =
      Number.isFinite(numeric) && asString !== '' && !Number.isNaN(numeric) && /^-?\d+(\.\d+)?$/.test(asString)
        ? new Date(numeric < 1e12 ? numeric * 1000 : numeric)
        : new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderTrainingCard = (training, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={training.modId || index}>
      <Card className={classes.trainingCard} onClick={() => handleTakeTraining(training)}>
        <Box 
          className={classes.thumbnail}
          style={{ 
            backgroundImage: `url(${training.thumbnail || '/api/placeholder/300/160'})`,
            backgroundColor: training.thumbnail ? 'transparent' : '#f5f5f5'
          }}
        >
          <Chip
            label={getTranslation(training.actualStatus, preferredLanguage)}
            size="small"
            className={classes.statusBadge}
            style={{ 
              backgroundColor: getStatusColor(training.actualStatus),
              color: 'white'
            }}
          />
          <Box className={classes.durationBadge}>
            ⏱️ {Math.round((training.timeEstimate || 1800) / 60)} min
          </Box>
        </Box>
        
        <CardContent style={{ padding: 16 }}>
          <Typography 
            variant="h6" 
            style={{ 
              fontWeight: 600, 
              marginBottom: 4,
              color: pageTitleColor,
              lineHeight: 1.3,
              height: 52,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {training.title || getTranslation(`Training Module ${index + 1}`, preferredLanguage)}
          </Typography>

          {(() => {
            const assignedDateLabel = formatTrainingAssignedDate(getTrainingAssignedDate(training));
            if (!assignedDateLabel) return null;
            return (
              <Typography
                variant="caption"
                style={{
                  display: 'block',
                  color: '#666',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                {`${getTranslation('Module Assigned', preferredLanguage)}: ${assignedDateLabel}`}
              </Typography>
            );
          })()}
          
          <Typography 
            variant="body2" 
            style={{ 
              color: '#666',
              marginBottom: 12,
              height: 60,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.4
            }}
          >
            {training.description ||
              getTranslation(
                'Comprehensive cybersecurity training module to enhance your security awareness and protect against cyber threats.',
                preferredLanguage
              )}
          </Typography>

          {training.progress > 0 && !training.isCompleted && (
            <Box className={classes.progressContainer}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" color="textSecondary">
                  {getTranslation('Progress', preferredLanguage)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {training.progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={training.progress} 
                style={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          <Box mt={2}>
            {training.isCompleted ? (
              <Box display="flex" gap={1}>
                <Button 
                  className={classes.completedButton}
                  fullWidth
                  startIcon={<PlayArrow />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleTakeTraining(training);
                  }}
                >
                  {getTranslation('Review Training', preferredLanguage)}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.retakeQuizButton}
                  fullWidth
                  startIcon={<Assignment />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleTakeQuiz(training);
                  }}
                >
                  {getTranslation('Retake Quiz', preferredLanguage)}
                </Button>
              </Box>
            ) : (
              <Button 
                className={classes.takeTrainingButton}
                fullWidth
                startIcon={<PlayArrow />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleTakeTraining(training);
                }}
              >
                {training.progress > 0 ? 
                  getTranslation('Continue Training', preferredLanguage) : 
                  getTranslation('Take Training', preferredLanguage)
                }
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  if (isLoading && !trainingData) {
    return <LoadingSpinner className="LinkTableSpinner" box="LinkTableSpinnerBox" />;
  }

  return (
    <div className={classes.root}>
      {/* Hero Section */}
      <Paper className={classes.heroSection}>
        <div className={classes.heroContent}>
          <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 16 }}>
            🎓 {getTranslation('My Training Dashboard', preferredLanguage)}
          </Typography>
          <Typography variant="body1" style={{ opacity: 0.9, marginBottom: 24 }}>
            {getTranslation(
              'Complete your assigned cybersecurity training modules and track your progress',
              preferredLanguage
            )}
          </Typography>
          
          {/* Quick Stats */}
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                  {stats.completionRate}%
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  {getTranslation('Complete', preferredLanguage)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                  {stats.completed}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  {getTranslation('Completed', preferredLanguage)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                  {stats.assigned}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  {getTranslation('Assigned', preferredLanguage)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                  {stats.available}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  {getTranslation('Available', preferredLanguage)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Paper>

      {/* Filter Bar */}
      <Paper className={classes.filterBar}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Box className={classes.filterChips}>
            {['All', 'Assigned', 'Complete'].map((filter) => (
              <Chip
                key={filter}
                label={getTranslation(filter, preferredLanguage)}
                className={`${classes.filterChip} ${filterType === filter ? 'active' : ''}`}
                onClick={() => setFilterType(filter)}
              />
            ))}
          </Box>
          
          <Typography variant="body2" color="textSecondary">
            {filteredTraining.length} {getTranslation('modules found', preferredLanguage)}
          </Typography>
        </Box>
      </Paper>

      {/* Training Modules */}
      <Grid container spacing={3}>
        {filteredTraining.length > 0 ? (
          filteredTraining.map(renderTrainingCard)
        ) : (
          <Grid item xs={12}>
            <Paper style={{ padding: 32, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                {getTranslation('No training modules found for the selected filter', preferredLanguage)}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {getTranslation('Check back later or contact your administrator', preferredLanguage)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default UserTrainingInterface;